import { useState, useRef, useCallback, useEffect, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ReactFlowProvider } from "reactflow";
import { useAuth0 } from "@auth0/auth0-react";
import SoftBox from "components/SoftBox";
import ComponentPropertiesPanel from "../ComponentPropertiesPanel";
import TriggerPropertiesPanel from "../TriggerPropertiesPanel";
import ContextPanel from "../ContextPanel";
import { FlowContext, FlowContextApi } from "../../context";
import {
  setNumberOfNodesLimitReached,
  clearCurrentSavedFlow,
  clearFlow
} from "redux/actions/flowDesigner/flowsActionCreator";
import { clearFlowTriggers } from "redux/actions/flowDesigner/triggersActionCreator";
import { NODE_TYPES } from "constants/flowDesigner";
import "reactflow/dist/style.css";
import "../../css/index.css";
import AlertDialog from "cxfcomponents/AlertDialog";
import NotesDialog from "layouts/Flow Designer/components/NotesDialog";
import { useIntegrations } from "hooks/useIntegrations";
import ExecutionReportPanel from "../ExecutionReportPanel";
import SharerFlowNotificationDialog from "../../../Flows/components/SharerFlowNotificationDialog";
import { executorSocket } from "layouts/Flow Designer/components/Sockets";
import { openFlows, thisUsersData } from "App";
import StartNodePanel from "../StartNodePanel";
import { Actions, Layout } from "flexlayout-react";
import "flexlayout-react/style/light.css";
import { useFlexLayout } from "../../../../context/AppProvider/FlexLayoutContext";
import { DesignerHomeTab } from "../Home";
import { FlowCanvas } from "./components/canvas";

const position = { x: 0, y: 0 };

const DnDFlow = ({ zoomOnScroll, ref }) => {
  useIntegrations();
  const dispatch = useDispatch();
  const { currentSavedFlow, numberOfNodesLimitReached, isFlowLoading } = useSelector(
    (store) => store?.designer?.flows
  );
  const { viewOptions } = useSelector((store) => store?.designer?.settings);
  const { t } = useTranslation("flowDesigner");
  const { user } = useAuth0();
  const reactFlowWrapper = useRef(null);
  const { model, requestCloseTab } = useFlexLayout();
  const [showHome, setShowHome] = useState(false);
  const flowJoined = useRef(false);

  const closeNumberOfNodesLimitAlertDialog = () => {
    dispatch(setNumberOfNodesLimitReached(false));
  };

  const handleFlexAction = useCallback(
    (action) => {
      if (action.type === "FlexLayout_DeleteTab") {
        const tabNode = action.data?.node;
        const tabId = typeof tabNode === "string" ? tabNode : tabNode?.getId?.();

        if (action.data?.source === "confirmed") {
          return action;
        }

        const allowed = requestCloseTab(tabId);
        if (!allowed) {
          return undefined;
        }
      }

      if (action.type === "FlexLayout_DeleteTabset" || action.type === "FlexLayout_DeleteTab") {
        requestAnimationFrame(() => {
          const root = model.getRoot();
          const tabsets = root.getChildren();

          const allTabs = tabsets.flatMap((ts) => (ts.getChildren ? ts.getChildren() : []));

          const hasReactFlow = allTabs.some((t) => t.getComponent() === "reactflow");

          if (!hasReactFlow) {
            const tabsToDelete = allTabs.filter((t) => t.getComponent() !== "home");

            for (const tab of tabsToDelete) {
              model.doAction({ type: "FlexLayout_DeleteTab", data: { node: tab } });
            }

            dispatch(clearFlowTriggers());
            dispatch(clearFlow());
            dispatch(clearCurrentSavedFlow());

            setShowHome(true);
          }
        });
      }

      return action;
    },
    [model, dispatch, requestCloseTab]
  );

  const handleTabAuxMouseClick = useCallback(
    (node, event) => {
      if (event.button !== 1 || node.getType() !== "tab") return;

      event.preventDefault();
      event.stopPropagation();
      model.doAction(Actions.deleteTab(node.getId()));
    },
    [model]
  );

  const preventTabAutoScroll = useCallback((event) => {
    if (event.button !== 1 || !event.target.closest?.(".flexlayout__tab_button")) return;

    event.preventDefault();
  }, []);

  // FlexLayout factory function
  const factory = useCallback(
    (node) => {
      const comp = node.getComponent();
      const tabId = node.getId();

      switch (comp) {
        case "reactflow":
          return (
            <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ height: "100%" }}>
              {isFlowLoading ? (
                <></>
              ) : (
                <>
                  <FlowCanvas zoomOnScroll={zoomOnScroll} tabId={tabId} node={node} ref={ref} />
                </>
              )}
            </div>
          );
        case "componentProps":
          return <ComponentPropertiesPanel key={tabId} tabId={tabId} node={node} />;
        case "triggerProps":
          return <TriggerPropertiesPanel trigger={node.getConfig()?.trigger} />;
        case "context":
          return <ContextPanel />;
        case "executionReport":
          return <ExecutionReportPanel node={node} />;
        case "startNode":
          return <StartNodePanel />;
        case "home":
          return <DesignerHomeTab />;
        default:
          return <div>{node.getName()}</div>;
      }
    },
    [isFlowLoading, currentSavedFlow, zoomOnScroll, viewOptions, dispatch]
  );

  useEffect(() => {
    const joinFlowRoom = () => {
      if (flowJoined.current) return;

      executorSocket.emit("joinFlowRoom", {
        tenantId: currentSavedFlow.tenant_id,
        flowId: currentSavedFlow.id,
        user: thisUsersData
      });
      openFlows.set(currentSavedFlow.id, currentSavedFlow.tenant_id);
      flowJoined.current = true;
    };

    if (executorSocket?.connected) {
      joinFlowRoom();
    } else {
      executorSocket?.once("connect", joinFlowRoom);
    }

    return () => {
      executorSocket?.off("connect", joinFlowRoom);

      if (flowJoined.current) {
        executorSocket.emit("leaveFlowRoom", {
          tenantId: currentSavedFlow.tenant_id,
          flowId: currentSavedFlow.id,
          userId: user.cxfUserId
        });
        openFlows.delete(currentSavedFlow.id);
        flowJoined.current = false;
      }
    };
  }, [currentSavedFlow?.id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!executorSocket?.connected) return;

      if (flowJoined.current) {
        executorSocket.emit("leaveFlowRoom", {
          tenantId: currentSavedFlow.tenant_id,
          flowId: currentSavedFlow.id,
          userId: user.cxfUserId
        });
        openFlows.delete(currentSavedFlow.id);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [executorSocket, currentSavedFlow.id, user.cxfUserId]);

  return (
    <SoftBox sx={{ height: "100%", minHeight: 0 }}>
      {/* <HiddenButtons undo={undo} redo={redo} /> */}
      <SoftBox className="dndflow" sx={{ height: "100%" }} onMouseDownCapture={preventTabAutoScroll}>
        {showHome ? (
          <DesignerHomeTab />
        ) : (
          <Layout
            model={model}
            factory={factory}
            onAction={handleFlexAction}
            onAuxMouseClick={handleTabAuxMouseClick}
          />
        )}
        <AlertDialog
          open={numberOfNodesLimitReached}
          onClose={closeNumberOfNodesLimitAlertDialog}
          dialogTitle={t("dialogs.node_limit.title")}
          dialogContent={t("dialogs.node_limit.content")}
          closeButtonVariant="contained"
        />
        <NotesDialog />
        <SharerFlowNotificationDialog />
      </SoftBox>
    </SoftBox>
  );
};

const Flow = ({ref}) => {
  const [zoomOnScroll, setZoomOnScroll] = useState(true);

  return (
    <ReactFlowProvider>
      <FlowContext.Provider value={{}}>
        <FlowContextApi.Provider value={{ setZoomOnScroll }}>
          <DnDFlow zoomOnScroll={zoomOnScroll} ref={ref}/>
        </FlowContextApi.Provider>
      </FlowContext.Provider>
    </ReactFlowProvider>
  );
};

export default memo(Flow);

export const initialNodes = [
  {
    id: "1",
    type: NODE_TYPES.STATIC,
    data: { label: "Start", isStart: true },
    position,
    deletable: false
  },
  {
    id: "2",
    type: NODE_TYPES.STATIC,
    data: { label: "End", isStart: false },
    position,
    deletable: false
  }
];

export const initialEdges = [
  {
    id: "1->2",
    source: "1",
    target: "2"
  }
];
